import { Component, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import { BehaviorSubject } from 'rxjs';

/**
 * idle       — no newer version detected
 * offer      — toast asking the user to refresh
 * confirm    — refresh requested while a page reports unsaved changes
 * minimized  — toast dismissed; header shows the "Update ready" pill
 * refreshing — reload in progress
 */
export type VersionUpdateState = 'idle' | 'offer' | 'confirm' | 'minimized' | 'refreshing';

/**
 * Non-blocking "new version available" notice. The user decides when to refresh;
 * dismissing the toast leaves a persistent pill in the header until they do.
 *
 * Declared alongside the toast component to avoid a circular import between the two.
 */
@Injectable({ providedIn: 'root' })
export class VersionUpdateService {
   readonly state$ = new BehaviorSubject<VersionUpdateState>( 'idle' );

   private toastRef: MatSnackBarRef<VersionExpiredDialogComponent> | null = null;
   private unsavedChecks = new Set<() => boolean>();

   constructor( private snackbar: MatSnackBar ) { }

   get state(): VersionUpdateState { return this.state$.getValue(); }

   /** Called when the backend reports VERSION_EXPIRED. Repeat reports collapse into one notice. */
   notify(): void {
      if ( this.state === 'idle' ) {
         this.open();
      }
   }

   /** Shows (or re-shows, from the header pill) the toast. */
   open(): void {
      if ( this.state === 'refreshing' ) { return; }

      this.state$.next( 'offer' );

      if ( this.toastRef ) { return; }

      this.toastRef = this.snackbar.openFromComponent( VersionExpiredDialogComponent, {
         horizontalPosition: 'end',
         verticalPosition: 'bottom',
         panelClass: 'version-update-toast-panel',
      });

      this.toastRef.afterDismissed().subscribe(() => {
         this.toastRef = null;

         // Another snackbar can replace this one; keep the update reachable from the header.
         if ( this.state === 'offer' || this.state === 'confirm' ) {
            this.state$.next( 'minimized' );
         }
      });
   }

   /** "Later", the dismiss button and "Keep working" all minimize to the header pill. */
   later(): void {
      this.state$.next( 'minimized' );
      this.toastRef?.dismiss();
   }

   refresh(): void {
      if ( this.state === 'offer' && this.hasUnsavedChanges() ) {
         this.state$.next( 'confirm' );

         return;
      }

      this.state$.next( 'refreshing' );
      this.reloadPage();
   }

   /**
    * Lets a page report unsaved work so refreshing asks for confirmation first.
    * Returns a function that removes the check (call it in ngOnDestroy).
    */
   registerUnsavedCheck( check: () => boolean ): () => void {
      this.unsavedChecks.add( check );

      return () => this.unsavedChecks.delete( check );
   }

   hasUnsavedChanges(): boolean {
      return Array.from( this.unsavedChecks ).some( check => check() );
   }

   protected reloadPage(): void {
      location.reload();
   }
}

@Component( {
   selector: 'lib-version-expired-dialog',
   template: `
      <div class="version-update-toast" *ngIf="update.state$ | async as state">
         <span class="version-update-icon" color="primary" [class.refreshing]="state === 'refreshing'" aria-hidden="true">
            <mat-icon>refresh</mat-icon>
         </span>

         <div class="version-update-body" [ngSwitch]="state">
            <ng-container *ngSwitchCase="'confirm'">
               <strong class="version-update-title">You have unsaved changes</strong>
               <span class="version-update-text">Refreshing now will clear your current changes on this page.</span>
               <div class="version-update-actions">
                  <button mat-stroked-button type="button" class="version-update-secondary" (click)="update.later()">Keep working</button>
                  <button mat-flat-button type="button" color="primary" (click)="update.refresh()">Refresh anyway</button>
               </div>
            </ng-container>

            <ng-container *ngSwitchCase="'refreshing'">
               <strong class="version-update-title">Updating the portal…</strong>
               <span class="version-update-text">This takes a few seconds.</span>
            </ng-container>

            <ng-container *ngSwitchDefault>
               <strong class="version-update-title">A new version is available</strong>
               <span class="version-update-text">Refresh when you reach a good stopping point. You can keep working in the meantime.</span>
               <div class="version-update-actions">
                  <button mat-stroked-button type="button" class="version-update-secondary" (click)="update.later()">Later</button>
                  <button mat-flat-button type="button" color="primary" (click)="update.refresh()">Refresh now</button>
               </div>
            </ng-container>
         </div>

         <button *ngIf="state === 'offer'" mat-icon-button type="button" class="version-update-close"
            aria-label="Dismiss — remind me later" (click)="update.later()">
            <mat-icon>close</mat-icon>
         </button>
      </div>`
} )
export class VersionExpiredDialogComponent {
   constructor( public update: VersionUpdateService ) { }
}
